import React from 'react';

import { CaseHistorySection } from './sections/CaseHistorySection';
import { PureToneSection } from './sections/PureToneSection';
import { TuningForkSection } from './sections/TuningForkSection';
import { OtoscopySection } from './sections/OtoscopySection';
import { SpeechSection } from './sections/SpeechSection';
import { ResultsGridSection } from './sections/ResultsGridSection';
import { RecommendationsAdviceSection } from './sections/RecommendationsAdviceSection';
import { ProvisionalDiagnosisSection } from './sections/ProvisionalDiagnosisSection';
import { GenericClinicalSection } from './sections/GenericClinicalSection';
import { OAEReportSection } from './sections/OAEReportSection';
import { SpecialReportSection } from './sections/SpecialReportSection';
import { TinnitogramReportSection } from './sections/TinnitogramReportSection';
import { TympanometryInlineSection } from './TympanometrySections';
import ABRWaveformCanvas from '../ABRWaveformCanvas';
import SoundFieldMiniAudiogram from '../SoundFieldMiniAudiogram';

/**
 * Section registry — one entry per toggleable section id. Each entry is a
 * pure render function `(ctx) => ReactNode | null` that receives the shared
 * report context from ReportsPanel. Keeping this map flat + data-driven lets us
 * add/remove sections without touching the main panel, and avoids a
 * 40-line switch statement for every new clinical tab.
 *
 * A section returning `null` is silently skipped — useful when a dependency
 * flag hides it (e.g. Tympanometry inline is suppressed when a separate page
 * is active, or Tuning Fork mini-table only appears in full mode).
 */
export const SECTION_REGISTRY = {
  case_history: (ctx) => <CaseHistorySection narrative={ctx.caseHistoryNarrative} />,

  pure_tone: (ctx) => {
    // Only apply custom audiogram size when there is vertical room (i.e. Tymp
    // on a separate page). Otherwise stay "standard" to preserve A4 fit.
    const effectiveSize = ctx.useSeparatePage ? ctx.audiogramSize : 'standard';
    const tfSectionEnabled = ctx.isEnabled('tuning_fork');
    return (
      <PureToneSection
        rightEar={ctx.rightEarData}
        leftEar={ctx.leftEarData}
        mode={ctx.audiogramMode}
        tuningFork={ctx.preTestData?.tuning_fork}
        showTuningForkMini={tfSectionEnabled && !ctx.tuningForkFull}
        size={effectiveSize}
      />
    );
  },

  tuning_fork: (ctx) =>
    ctx.tuningForkFull ? (
      <TuningForkSection
        tf={ctx.preTestData?.tuning_fork}
        showABC={ctx.showABC}
        showBing={ctx.showBing}
      />
    ) : null,

  otoscopy: (ctx) => <OtoscopySection ot={ctx.preTestData?.otoscopy} />,
  speech:   (ctx) => <SpeechSection speech={ctx.speechData} />,

  // Generic clinical tabs — each pulls from its own Dict and impression field.
  special_tests: (ctx) => {
    // 2026-02-18 · Special Tests overhaul — prefer the rich TDT/SISI/ABLB
    // renderer. When only the legacy `fields` bag is populated (from the
    // pre-overhaul flat form), fall back to the generic renderer so
    // historical sessions keep printing what they originally captured.
    const d = ctx.specialTestsData || {};
    const legacyFields = d.fields || {};
    const hasNew = !!(d.tdt || d.sisi || d.ablb || d.overall_conclusion || d.recommendations);
    if (!hasNew && Object.keys(legacyFields).length > 0) {
      return <GenericClinicalSection title="Special Diagnostic Tests" data={ctx.specialTestsData} impressionKey="st_impression" />;
    }
    return <SpecialReportSection data={ctx.specialTestsData} />;
  },
  oae: (ctx) => {
    // 2026-02-18 · OAE overhaul — prefer the rich TEOAE/DPOAE renderer
    // (graph + per-frequency table). When only the legacy `fields` bag
    // is populated (from the pre-overhaul flat form), fall back to the
    // generic renderer so historical sessions keep printing what they
    // originally captured.
    const d = ctx.oaeData || {};
    const legacyFields = d.fields || {};
    const hasLegacyOnly = !d.right && !d.left && !d.test_type
      && !d.probe_type && !d.stimulus && !d.equipment
      && Object.keys(legacyFields).length > 0;
    if (hasLegacyOnly) {
      return <GenericClinicalSection title="Otoacoustic Emissions" data={ctx.oaeData} impressionKey="oae_impression" />;
    }
    return <OAEReportSection oae={ctx.oaeData} />;
  },
  soundfield: (ctx) => {
    const hasAnyField = Object.values(ctx.soundfieldData?.fields || {}).some((v) => v && String(v).trim() !== '');
    if (!hasAnyField) return null;
    return (
      <div>
        <div className="mb-1">
          <div className="h-[180px] border border-gray-400 bg-white">
            <SoundFieldMiniAudiogram fields={ctx.soundfieldData?.fields || {}} height={180} />
          </div>
        </div>
        <GenericClinicalSection title="Sound Field / Aided" data={ctx.soundfieldData} impressionKey="sf_impression" />
      </div>
    );
  },
  abr: (ctx) => {
    const hasAnyField = Object.values(ctx.abrData?.fields || {}).some((v) => v && String(v).trim() !== '');
    if (!hasAnyField) return null;
    return (
      <div>
        <div className="mb-1">
          <div className="h-[180px] border border-gray-400 bg-white">
            <ABRWaveformCanvas fields={ctx.abrData?.fields || {}} height={180} />
          </div>
        </div>
        <GenericClinicalSection title="ABR / ASSR" data={ctx.abrData} impressionKey="abr_impression" />
      </div>
    );
  },
  pediatric: (ctx) => (
    <GenericClinicalSection title="Pediatric Audiometry" data={ctx.pediatricData} impressionKey="ped_impression" />
  ),
  tinnitus: (ctx) => {
    // 2026-02-18 · Tinnitogram overhaul — prefer the rich three-sub-test
    // renderer. When only the legacy `fields` bag is populated (pre-
    // overhaul flat form), fall back to the generic renderer.
    const d = ctx.tinnitusData || {};
    const legacyFields = d.fields || {};
    const hasNew = !!(d.pitch || d.loudness || d.masking || d.summary || d.clinical_interpretation || d.recommendations);
    if (!hasNew && Object.keys(legacyFields).length > 0) {
      return <GenericClinicalSection title="Tinnitus Assessment" data={ctx.tinnitusData} impressionKey="tin_impression" />;
    }
    return <TinnitogramReportSection data={ctx.tinnitusData} />;
  },

  // Tymp is suppressed on main page when it's moved to a separate page.
  tympanometry: (ctx) =>
    ctx.useSeparatePage ? null : <TympanometryInlineSection impedance={ctx.impedanceData} />,

  results:         (ctx) => <ResultsGridSection entries={ctx.buildResultEntries()} />,
  provisional_diagnosis: (ctx) => (
    <ProvisionalDiagnosisSection text={ctx.provisionalDiagnosis} />
  ),
  recommendations: (ctx) => (
    <RecommendationsAdviceSection
      recommendations={ctx.recText}
      advice={ctx.furtherAdvice}
    />
  ),
};

// Render a single section by id given the full context. Returns null if either
// the id isn't registered or the section's render function opts out.
export const renderSectionById = (id, ctx) => {
  const fn = SECTION_REGISTRY[id];
  if (!fn) return null;
  const node = fn(ctx);
  if (!node) return null;
  // Auto-attach a stable key so callers can map() without wrappers.
  return React.cloneElement(node, { key: id });
};
